import { useState, FormEvent } from "react";
import { X, Gift, Mail, User, Sparkles } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface DiscountPopupProps {
  onClose: () => void;
}

const DiscountPopup = ({ onClose }: DiscountPopupProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim()) {
      toast({
        title: "Campos requeridos",
        description: "Por favor completa todos los campos",
        variant: "destructive"
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Email inválido",
        description: "Por favor ingresa un email válido",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke("send-discount-code", {
        body: { name: name.trim(), email: email.trim() }
      });

      if (error) throw error;

      if (data?.success) {
        setIsSuccess(true);
        localStorage.setItem("gordon_discount_claimed", "true");
        localStorage.removeItem("gordon_discount_dismissed_at");
        
        toast({
          title: "¡Código enviado!",
          description: "Revisa tu email para ver tu código de descuento",
        });
      } else {
        throw new Error(data?.error || "Error al procesar la solicitud");
      }
    } catch (error) {
      console.error("Error claiming discount:", error);
      toast({
        title: "Error",
        description: "Hubo un problema al enviar el código. Por favor intenta de nuevo.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSuccess) {
      localStorage.setItem("gordon_discount_dismissed_at", Date.now().toString());
    }
    onClose();
  };

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-[hsl(var(--sage-green))] to-[hsl(var(--sky-blue))] border-2 border-primary/20">
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Cerrar</span>
        </button>

        {isSuccess ? (
          <div className="text-center py-8 px-4">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
              <Mail className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">¡Código Enviado!</h2>
            <p className="text-muted-foreground mb-4">
              Tu código de <span className="font-semibold text-primary">15% de descuento</span> ha sido enviado a:
            </p>
            <p className="font-medium text-lg bg-white/80 rounded-lg py-3 px-4 mb-6">
              {email}
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Revisa tu bandeja de entrada. Si no lo encuentras, verifica la carpeta de spam.
            </p>
            <Button onClick={handleClose} className="w-full">
              Continuar comprando
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 pt-6">
            <div className="text-center space-y-3">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-2">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold">🎁 ¡15% de Descuento!</h2>
              <p className="text-sm text-muted-foreground">
                Suscríbete y recibe un código exclusivo para tu primera compra
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Tu nombre
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Nombre completo"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={100}
                  className="bg-white/80 backdrop-blur-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Tu email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  maxLength={255}
                  className="bg-white/80 backdrop-blur-sm"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full text-lg py-6"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-pulse">Enviando...</span>
                </>
              ) : (
                <>
                  <Gift className="w-5 h-5 mr-2" />
                  Obtener mi descuento
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Al suscribirte aceptas recibir emails con ofertas especiales
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DiscountPopup;
